import { useCallback, useMemo } from 'react';
import { Setting } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { settingsRouteLookup } from '@frontend/settings-routing';
import { useWeaveAssistantSettingsFeatureFlag } from '@frontend/weave-assistant-hooks';

// TODO: remove this old hook when semantic-search FF is fully implemented
/**
 * This hook filters settings using the fuse settings that we set up in the instantiated RoutePathLookup class.
 * @param search The search term to filter settings by.
 * @returns An array of filtered settings based on the search word and user permission and feature flags, each containing a labelPath and path.
 * @deprecated This hook is deprecated and will be removed in the future. Use `useFilterSettings` instead.
 */
export const useFilterSettingsOld = (search: string): { labelPath: string; path: string }[] => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasPhonefullAccess = hasSchemaACL(selectedLocationIds[0], Permission.PHONE_FULL_ACCESS);

  const filteredSettings = useMemo(() => {
    const settings = settingsRouteLookup.searchKeyword(search).map((item) => item.item);
    return settings.filter((setting) => {
      if (setting.path === '/settings/phone/call-routes/') {
        return hasPhonefullAccess;
      }
      if (setting.path === '/settings/phone/call-recording/') {
        return hasPhonefullAccess;
      }
      return true; // Allow all other settings
    });
  }, [hasPhonefullAccess, search]);

  return filteredSettings;
};

/**
 * This hook filters settings based on the user's permissions and feature flags.
 * @param settings An array of Setting objects to filter.
 * @returns An array of filtered settings based on the user's permissions and feature flags.
 * */
export const useFilterSettings = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasPhonefullAccess = hasSchemaACL(selectedLocationIds[0], Permission.PHONE_FULL_ACCESS);
  const hasWeaveAssistantSettings = useWeaveAssistantSettingsFeatureFlag();

  const filterSetting = useCallback(
    (settings: Setting[]) => {
      return settings.filter((setting) => {
        // Check for Weave Assistant settings feature flag
        if (setting.path.includes('/settings/weave-assistant/')) {
          return hasWeaveAssistantSettings;
        }
        if (setting.path === '/settings/phone/call-routes/') {
          return hasPhonefullAccess;
        }
        if (setting.path === '/settings/phone/call-recording/') {
          return hasPhonefullAccess;
        }
        return true; // Allow all other settings
      });
    },
    [hasPhonefullAccess, hasWeaveAssistantSettings]
  );

  return { filterSetting };
};
